import { ModelName } from "./ModelName";

/**
 * Maximum input-token budget for each model.
 *
 * Mirrors the backend's CORTEVA_AVAILABLE_MODELS config. Drift is possible
 * when a model is added or a window changes — see ADR 034 for the rationale
 * behind the frontend-maintained table.
 *
 * Models not listed here will cause `getModelMaxInputTokens` to return
 * `null`, which the context-used gauge treats as "fail closed" (hide).
 */
export const MODEL_MAX_INPUT_TOKENS: Partial<Record<ModelName, number>> = {
  [ModelName.GPT_35_TURBO]: 16_385 - 4_096,
  [ModelName.GPT_4_TURBO]: 128_000 - 4_096,
  [ModelName.GPT_4O]: 128_000 - 16_384,
  [ModelName.GPT_4O_MINI]: 128_000 - 16_384,
  [ModelName.GPT_41]: 1_047_576 - 32_768,
  [ModelName.GPT_41_MINI]: 128_000 - 32_768,
  [ModelName.GPT_41_NANO]: 128_000 - 32_768,
  [ModelName.GPT_5]: 400_000 - 32_000,
  [ModelName.GPT_5_MINI]: 400_000 - 32_000,
  [ModelName.GPT_5_NANO]: 400_000 - 32_000,
  [ModelName.GPT_54]: 1_050_000 - 64_000,
  [ModelName.GPT_54_MINI]: 400_000 - 32_000,
  [ModelName.GPT_54_NANO]: 400_000 - 32_000,
  [ModelName.GPT_55]: 1_050_000 - 64_000,
  [ModelName.GPT_56_LUNA]: 1_050_000 - 64_000,
  [ModelName.GPT_56_TERRA]: 1_050_000 - 64_000,
  [ModelName.GPT_56_SOL]: 1_050_000 - 64_000,
  [ModelName.O1_PREVIEW]: 128_000 - 32_768,
  [ModelName.O1]: 200_000 - 32_768,
  [ModelName.O1_MINI]: 128_000 - 32_768,
  [ModelName.O3]: 200_000 - 32_768,
  [ModelName.O3_MINI]: 200_000 - 32_768,
  [ModelName.O4_MINI]: 200_000 - 32_768,
  [ModelName.GEMINI_1_5_PRO_002]: 2_097_152 - 8_192,
  [ModelName.GEMINI_2_0_FLASH]: 1_048_576 - 8_192,
  [ModelName.GEMINI_2_5_PRO_PREVIEW]: 1_048_576 - 65_536,
  [ModelName.GEMINI_2_5_PRO]: 1_048_576 - 65_536,
  [ModelName.GEMINI_2_5_FLASH_PREVIEW]: 1_048_576 - 65_536,
  [ModelName.GEMINI_2_5_FLASH]: 1_048_576 - 65_536,
  [ModelName.GEMINI_2_5_FLASH_IMAGE]: 32_000 - 8_192,
  [ModelName.GEMINI_3_PRO_PREVIEW]: 1_048_576 - 65_536,
  [ModelName.GEMINI_3_PRO_IMAGE]: 65_536 - 32_768,
  [ModelName.GEMINI_3_1_PRO_PREVIEW]: 1_048_576 - 65_536,
  [ModelName.GEMINI_3_1_FLASH_IMAGE]: 131_072 - 32_768,
  [ModelName.GEMINI_3_FLASH_PREVIEW]: 1_048_576 - 65_536,
  [ModelName.GEMINI_3_5_FLASH]: 1_048_576 - 65_536,
  [ModelName.GEMINI_3_8_FLASH]: 1_048_576 - 65_536,
  [ModelName.GEMINI_4_ARGON]: 1_048_576 - 65_536,
  [ModelName.CLAUDE_4_SONNET]: 200_000 - 32_000,
  [ModelName.CLAUDE_4_5_SONNET]: 1_000_000 - 32_000,
  [ModelName.CLAUDE_4_6_SONNET]: 1_000_000 - 64_000,
  [ModelName.CLAUDE_5_SONNET]: 1_000_000 - 64_000,
  [ModelName.CLAUDE_4_OPUS]: 200_000 - 32_000,
  [ModelName.CLAUDE_4_1_OPUS]: 200_000 - 32_000,
  [ModelName.CLAUDE_4_5_OPUS]: 200_000 - 32_000,
  [ModelName.CLAUDE_4_6_OPUS]: 200_000 - 32_000,
  [ModelName.CLAUDE_4_7_OPUS]: 200_000 - 32_000,
  [ModelName.CLAUDE_4_8_OPUS]: 200_000 - 32_000,
  [ModelName.CLAUDE_5_OPUS]: 200_000 - 32_000,
};

export function getModelMaxInputTokens(modelName: ModelName): number | null {
  return MODEL_MAX_INPUT_TOKENS[modelName] ?? null;
}
